import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { map, last, first, filter, findIndex } from 'lodash/fp';
import { VectorDirection } from '@mews/enums';
import { Identifiable } from '@mews/std';
import { DateTime, DateTimeService, Month, MonthService, useFeatureFlag } from '@mews/framework';
import { ApplicationState, WithServiceId, WithTimeUnit } from '@mews-commander/types';
import { FeatureFlag } from '@mews-commander/enums';
import { dateTimeToUtc, useCommanderSelector } from '@mews-commander/utils';
import {
    getTimeUnitByServiceId,
    getIsOverlappingNextTimeUnit,
} from '@mews-commander/legacy-entities/src/ducks/services';
import { CELL_HEIGHT, CELL_WIDTH } from '../../../constants';
import { WithFormArrayFields, WithDateTimeRangeArray } from '../../../types';
import { getAdjustmentSelector, getDateTimeRangeArray, getMonth } from '../../../selectors';
import { BlockSetupMatrixElement } from './styles';
import { renderAside } from './renderes/renderAside';
import { renderHeader } from './renderes/renderHeader';
import { RenderVector } from './renderes/renderVector';

interface BlockSetupMatrixProps
    extends Identifiable,
        WithServiceId,
        WithTimeUnit,
        WithFormArrayFields,
        WithDateTimeRangeArray {
    isEditingAvailability: boolean;
    isOverlappingNextTimeUnit: boolean;
    initialResourceCategories: Array<string>;
    month: Month | null;
}

const filterDaysInMonth = (month: Month, dateTimeRange: Array<DateTime>) =>
    filter((dateTime) => MonthService.isEqual(month, DateTimeService.toMonth(dateTime)), dateTimeRange);

const BlockSetupMatrix = ({
    fields,
    dateTimeRangeArray,
    id,
    month,
    isEditingAvailability,
    isOverlappingNextTimeUnit,
    serviceId,
    timeUnit,
    initialResourceCategories,
}: BlockSetupMatrixProps) => {
    const occupancyInBlocksEnabled = useFeatureFlag(FeatureFlag.occupancyInBlocksEnabled);
    const dateTimeRange = month ? filterDaysInMonth(month, dateTimeRangeArray) : dateTimeRangeArray;

    const firstInRange = first<DateTime>(dateTimeRange);
    const lastInRange = last<DateTime>(dateTimeRange);
    const lastInRangeArray = last<DateTime>(dateTimeRangeArray);

    const withCheckoutColumn =
        (lastInRange &&
            lastInRangeArray &&
            DateTimeService.isEqual(lastInRange, lastInRangeArray) &&
            isOverlappingNextTimeUnit) ||
        false;

    const utcRangeArray = map(dateTimeToUtc, dateTimeRange);
    const startIndex = firstInRange ? findIndex(firstInRange, dateTimeRangeArray) : 0;
    const adjustmentSelector = useCommanderSelector(getAdjustmentSelector);

    return (
        <BlockSetupMatrixElement
            asideCells={renderAside({
                fields,
                isEditingAvailability,
                serviceId,
                availabilityBlockId: id,
                initialResourceCategories,
            })}
            headerCells={renderHeader({ dateTimeRange, timeUnit, occupancyInBlocksEnabled, isEditingAvailability })}
            headerHeight={CELL_HEIGHT}
            cellHeight={CELL_HEIGHT}
            cellWidth={CELL_WIDTH}
            vectorDirection={VectorDirection.Row}
            vectors={RenderVector({
                fields,
                utcRangeArray,
                availabilityBlockId: id,
                occupancyInBlocksEnabled,
                serviceId,
                isEditingAvailability,
                withCheckoutColumn,
                startIndex,
                dateTimeRangeArray,
                adjustmentSelector,
            })}
        />
    );
};

type StateProps = Pick<
    BlockSetupMatrixProps,
    'dateTimeRangeArray' | 'month' | 'timeUnit' | 'isOverlappingNextTimeUnit'
>;

export default connect<StateProps, {}, Identifiable & WithServiceId, ApplicationState>(
    createStructuredSelector({
        dateTimeRangeArray: getDateTimeRangeArray,
        isOverlappingNextTimeUnit: (state, { serviceId }) => getIsOverlappingNextTimeUnit(state, { id: serviceId }),
        month: getMonth,
        timeUnit: getTimeUnitByServiceId,
    })
)(BlockSetupMatrix);
